import type {
  ManualOrderLine,
  SelectedProductRow,
} from '../types/manualOrder';

/** 빈/음수/NaN 은 0. 정수 금액·수량 파싱. */
export const parseAmount = (v: string | number | null | undefined): number => {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * 선택 상품 행의 단가를 도출한다. unitPrice override 가 있으면 그 값,
 * 없으면 카탈로그 listPrice(없으면 0). 화면 상품금액 합산·미리보기용.
 * (실제 자동조회는 BE 가 unitPrice 미지정 라인에 대해 수행하지만, 화면 합계는
 * listPrice 로 근사한다.)
 */
export const rowUnitPrice = (row: SelectedProductRow): number => {
  const override = parseAmount(row.unitPrice);
  if (override > 0) return override;
  return row.listPrice != null && row.listPrice > 0 ? row.listPrice : 0;
};

/** 상품금액 합산(Σ 단가 × 수량). */
export const computeItemAmount = (rows: ReadonlyArray<SelectedProductRow>): number =>
  rows.reduce((sum, row) => sum + rowUnitPrice(row) * Math.max(1, row.qty), 0);

/**
 * 결제금액 = 상품금액 − 할인 + 배송비. 음수 방지(0 하한).
 * (be-manual-order: payable = 상품합 − 할인 + 배송비.)
 */
export const computePayable = (
  itemAmount: number,
  discountAmount: number,
  shippingFee: number,
): number => Math.max(0, itemAmount - discountAmount + shippingFee);

/** 선택 행을 요청 라인으로 축약. unitPrice 미입력(=0)은 null(자동조회)로 전송. */
export const toRequestLines = (
  rows: ReadonlyArray<SelectedProductRow>,
): ManualOrderLine[] =>
  rows.map((row) => {
    const override = parseAmount(row.unitPrice);
    const line: ManualOrderLine = {
      productId: row.productId,
      qty: Math.max(1, row.qty),
    };
    if (row.variantId) line.variantId = row.variantId;
    if (override > 0) line.unitPrice = override;
    return line;
  });
